import { MODEL_CATALOG, findModel } from '../brain/catalog';
import type { BrainSelection, RemoteProvider } from '../brain/types';
import { AiThinkingTimer, formatMatchDuration } from './aiThinkingTimer';

export type AiPanelStatus = 'idle' | 'thinking' | 'result' | 'credential' | 'fallback';

export class AiPanelState {
  locked = false;
  status: AiPanelStatus = 'idle';
  message = '静候开局';

  constructor(
    public selectionId: string,
    private readonly thinkingTimer = new AiThinkingTimer(),
  ) {}

  select(id: string): boolean {
    if (this.locked) return false;
    this.selectionId = id;
    return true;
  }

  lock(): void { this.locked = true; }
  unlock(): void { this.locked = false; }

  startThinking(): void { this.thinkingTimer.start(); }
  currentThinkingMs(): number { return this.thinkingTimer.currentMs(); }
  completeThinking(): number { return this.thinkingTimer.complete(); }
  cancelThinking(): void { this.thinkingTimer.cancel(); }
  resetThinking(): void { this.thinkingTimer.reset(); }
  totalThinkingLabel(): string { return formatMatchDuration(this.thinkingTimer.totalMs); }

  showFallback(): void {
    this.status = 'fallback';
    this.message = '功力尽失，改为普通着法。';
  }
}

interface AiPanelCallbacks {
  onSelectionChange(selection: BrainSelection, wasLocked: boolean): boolean;
  onOpenCredentials(provider: RemoteProvider): void;
}

export const LOCAL_BRAIN_OPTIONS = [
  { id: 'local:beginner', label: '本地引擎 · 练气', level: 'beginner' as const },
  { id: 'local:intermediate', label: '本地引擎 · 筑基', level: 'intermediate' as const },
  { id: 'local:advanced', label: '本地引擎 · 金丹', level: 'advanced' as const },
  { id: 'local:nascentSoul', label: '本地引擎 · 元婴', level: 'nascentSoul' as const },
  { id: 'local:spiritSevering', label: '本地引擎 · 化神', level: 'spiritSevering' as const },
  { id: 'local:ascendant', label: '本地引擎 · 问鼎', level: 'ascendant' as const },
] as const;

export function selectionFromId(id: string): BrainSelection {
  const local = LOCAL_BRAIN_OPTIONS.find(option => option.id === id);
  if (local) return { kind: 'local', level: local.level };
  const model = findModel(id);
  return model ? { kind: 'remote', model } : { kind: 'local', level: 'intermediate' };
}

export class AiPanelController {
  readonly state = new AiPanelState('local:intermediate');
  private readonly select = document.getElementById('brain-select') as HTMLSelectElement;
  private readonly badge = document.getElementById('brain-provider') as HTMLSpanElement;
  private readonly statusEl = document.getElementById('brain-status') as HTMLSpanElement;
  private readonly lockEl = document.getElementById('brain-lock') as HTMLSpanElement;
  private readonly latencyEl = document.getElementById('brain-latency') as HTMLSpanElement;
  private readonly totalTimeEl = document.getElementById('brain-total-time') as HTMLSpanElement;
  private readonly moveEl = document.getElementById('brain-last-move') as HTMLSpanElement;
  private readonly reasonEl = document.getElementById('brain-reason') as HTMLParagraphElement;
  private readonly settingsButton = document.getElementById('brain-settings') as HTMLButtonElement;

  constructor(private readonly callbacks: AiPanelCallbacks) {
    this.populate();
    this.select.value = this.state.selectionId;
    this.select.addEventListener('change', () => {
      const requested = this.select.value;
      const wasLocked = this.state.locked;
      const selection = selectionFromId(requested);
      if (!this.callbacks.onSelectionChange(selection, wasLocked)) {
        this.select.value = this.state.selectionId;
        return;
      }
      if (wasLocked) this.state.unlock();
      this.state.select(requested);
      this.renderSelection();
    });
    this.settingsButton.addEventListener('click', () => {
      const selection = this.selection();
      this.callbacks.onOpenCredentials(selection.kind === 'remote' ? selection.model.provider : 'openai');
    });
    this.renderSelection();
  }

  selection(): BrainSelection { return selectionFromId(this.state.selectionId); }

  lock(): void {
    this.state.lock();
    this.lockEl.textContent = '本局已锁定';
    this.select.dataset.locked = 'true';
  }

  unlock(): void {
    this.state.unlock();
    this.lockEl.textContent = '开局前可切换';
    delete this.select.dataset.locked;
  }

  showIdle(): void { this.setStatus('idle', '静候落子'); }
  showThinking(): void { this.setStatus('thinking', '运功推演中'); }
  showCredentialRequired(): void { this.setStatus('credential', '需要配置模型密钥'); }

  startThinkingTimer(): void {
    this.state.startThinking();
    this.refreshThinkingTimer();
  }

  refreshThinkingTimer(): void {
    const elapsedMs = this.state.currentThinkingMs();
    if (elapsedMs > 0) this.latencyEl.textContent = `${elapsedMs} ms`;
  }

  completeThinkingTimer(): number {
    const elapsedMs = this.state.completeThinking();
    if (elapsedMs > 0) this.latencyEl.textContent = `${elapsedMs} ms`;
    this.renderTotalTime();
    return elapsedMs;
  }

  cancelThinkingTimer(): void {
    this.state.cancelThinking();
    this.latencyEl.textContent = '—';
  }

  resetThinkingTimer(): void {
    this.state.resetThinking();
    this.latencyEl.textContent = '—';
    this.renderTotalTime();
  }

  showResult(move: string, latencyMs: number, reason?: string): void {
    this.setStatus('result', '已落子');
    this.moveEl.textContent = move;
    this.latencyEl.textContent = latencyMs > 0 ? `${latencyMs} ms` : '本地';
    this.reasonEl.textContent = reason?.trim() || '未留下棋理说明。';
  }

  showFallback(move?: string, latencyMs?: number, detail?: string): void {
    this.state.showFallback();
    this.statusEl.textContent = this.state.message;
    this.statusEl.dataset.state = 'fallback';
    if (move) this.moveEl.textContent = move;
    if (latencyMs !== undefined) this.latencyEl.textContent = `${latencyMs} ms`;
    this.reasonEl.textContent = detail
      ? `${detail} 本地引擎已接手这一着。`
      : '外功不济，本地引擎接手这一着。';
  }

  private setStatus(status: AiPanelStatus, message: string): void {
    this.state.status = status;
    this.state.message = message;
    this.statusEl.textContent = message;
    this.statusEl.dataset.state = status;
  }

  private renderTotalTime(): void {
    this.totalTimeEl.textContent = `总计 ${this.state.totalThinkingLabel()}`;
  }

  private populate(): void {
    this.select.innerHTML = '';
    const localGroup = document.createElement('optgroup');
    localGroup.label = '本地棋手';
    for (const option of LOCAL_BRAIN_OPTIONS) localGroup.append(new Option(option.label, option.id));
    this.select.append(localGroup);

    for (const provider of ['openai', 'siliconflow'] as const) {
      const group = document.createElement('optgroup');
      group.label = provider === 'openai' ? 'OpenAI' : 'SiliconFlow';
      for (const model of MODEL_CATALOG.filter(item => item.provider === provider)) {
        group.append(new Option(model.displayName, model.id));
      }
      this.select.append(group);
    }
  }

  private renderSelection(): void {
    const selection = this.selection();
    this.badge.textContent = selection.kind === 'local'
      ? '本地'
      : selection.model.provider === 'openai' ? 'OpenAI' : 'SiliconFlow';
    this.badge.dataset.provider = selection.kind === 'local' ? 'local' : selection.model.provider;
    this.settingsButton.disabled = selection.kind === 'local';
    this.renderTotalTime();
    this.showIdle();
  }
}
